import React, { createContext, useContext, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchDePaulData } from "@/services/universityService";
import { createMetricsState } from "@/utils/metricTransformations";
import { MetricsState } from "@/types/metrics";

type University = {
  rank: number;
  name: string;
  score: number;
};

type RankingContextType = {
  depaulRank: number;
  setDepaulRank: React.Dispatch<React.SetStateAction<number>>;
  isSimulationMode: boolean;
  toggleSimulationMode: () => void;
  simulatedScore: number | null;
  setSimulatedScore: React.Dispatch<React.SetStateAction<number | null>>;
  currentMetrics: MetricsState | null;
  setCurrentMetrics: React.Dispatch<React.SetStateAction<MetricsState | null>>;

  // New states for scenario-based rank tracking
  previousScenarioRank: number | null;
  setPreviousScenarioRank: React.Dispatch<React.SetStateAction<number | null>>;
  currentSimulatedRank: number | null;
  setCurrentSimulatedRank: React.Dispatch<React.SetStateAction<number | null>>;
};

const RankingContext = createContext<RankingContextType | undefined>(undefined);

export function RankingProvider({ children }: { children: React.ReactNode }) {
  // The original DB baseline rank (e.g. from your supabase or wherever)
  const [depaulRank, setDepaulRank] = useState<number>(165);

  const [isSimulationMode, setIsSimulationMode] = useState<boolean>(false);
  const [simulatedScore, setSimulatedScore] = useState<number | null>(null);
  const [currentMetrics, setCurrentMetrics] = useState<MetricsState | null>(null);

  // NEW: track the rank from the previous scenario and the current scenario
  const [previousScenarioRank, setPreviousScenarioRank] = useState<number | null>(null);
  const [currentSimulatedRank, setCurrentSimulatedRank] = useState<number | null>(null);

  // Fetch initial DePaul data to set up metrics
  const { data: depaulData } = useQuery({
    queryKey: ['depaul'],
    queryFn: fetchDePaulData
  });

  // Initialize metrics when DePaul data is loaded
  useEffect(() => {
    if (depaulData) {
      const metricsState = createMetricsState(depaulData);
      setCurrentMetrics(metricsState);
      // Also set the DB baseline rank if the data has it
      if (depaulData.national_rank) {
        setDepaulRank(Number(depaulData.national_rank));
      }
    }
  }, [depaulData]);

  // Reset everything when we exit simulation mode
  const toggleSimulationMode = () => {
    setIsSimulationMode((prev) => {
      if (prev) {
        // Turn OFF simulation => Reset
        if (depaulData) {
          const metricsState = createMetricsState(depaulData);
          setCurrentMetrics(metricsState);
        }
        setSimulatedScore(null);
        setPreviousScenarioRank(null);
        setCurrentSimulatedRank(null);
      }
      return !prev;
    });
  };

  return (
    <RankingContext.Provider
      value={{
        depaulRank,
        setDepaulRank,
        isSimulationMode,
        toggleSimulationMode,
        simulatedScore,
        setSimulatedScore,
        currentMetrics,
        setCurrentMetrics,
        previousScenarioRank,
        setPreviousScenarioRank,
        currentSimulatedRank,
        setCurrentSimulatedRank
      }}
    >
      {children}
    </RankingContext.Provider>
  );
}

export function useRanking() {
  const context = useContext(RankingContext);
  if (!context) {
    throw new Error("useRanking must be used within a RankingProvider");
  }
  return context;
}
